import { Text } from "@chakra-ui/react";
import { useState } from "react";
import { useContext } from "react";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";

import { PublicationRead } from "@nens/rana/api-v1";
import { RanaTable, TrashIcon } from "@nens/ranalib";
import { TenantContext } from "@nens/ranalib";

import { useRanaSearchState } from "@/hooks/router/rana-search-state";
import { useRanaTableData } from "@/hooks/router/use-rana-table-data";
import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";
import { useIconSrc } from "@/hooks/use-icon-src";
import { Profile } from "@/lib/profile";
import { usePublicationListQuery } from "@/queries/query-publication";
import { getPrettyTime, getPrettyTimeFromNow } from "@/utils/get-pretty-time";

import { PublicationThumbnail } from "../map-thumbnail/publication-thumbnail";

import { PublicationDeleteModal } from "./components/publication-delete-modal";

export function PublicationList({
  setPublicationId,
  onOpenCreateModal,
}: {
  setPublicationId: (id: string) => void;
  onOpenCreateModal: () => void;
}) {
  const [publicationDeleteTargets, setPublicationDeleteTargets] = useState<
    PublicationRead[] | null
  >(null);
  const iconSrc = useIconSrc("publication");
  const { canWriteProject } = useProjectActionAllowed();
  const intl = useIntl();
  const { page, offset, limit, setPageSize, setPage } = useRanaTableData({
    keyAddon: "publications",
    initialPageSize: 10,
  });

  const [showOnlyMine, setShowOnlyMine] = useRanaSearchState("pubMine", false);
  const authInfo = useContext(TenantContext);
  const createdBy = showOnlyMine && !authInfo.isPublic ? authInfo.user.id : undefined;
  const publicationListQuery = usePublicationListQuery({
    offset,
    limit,
    createdBy,
  });

  const { isLoading, data } = publicationListQuery;
  const publications = data?.items ?? [];
  const tableDataIds = publications.map((item) => item.id);
  const numPages = Math.ceil((data?.total ?? 0) / limit);
  const deletePublicationText = intl.formatMessage({
    id: "publication.delete_publication",
    defaultMessage: "Delete publication",
  });
  const deletePublicationsText = intl.formatMessage({
    id: "publication.delete_publications",
    defaultMessage: "Delete publications",
  });

  const openDeleteModal = (targets: PublicationRead[]) => {
    if (!targets.length) return;

    setPublicationDeleteTargets(targets);
  };

  const tableActions = canWriteProject
    ? [
        {
          label: deletePublicationsText,
          action: (rowIds: string[]) =>
            openDeleteModal(publications.filter((item) => rowIds.includes(item.id))),
          icon: <TrashIcon />,
        },
      ]
    : [];

  return (
    <>
      {publicationDeleteTargets && (
        <PublicationDeleteModal
          publications={publicationDeleteTargets}
          onClose={() => setPublicationDeleteTargets(null)}
        />
      )}

      <RanaTable
        tableActions={tableActions}
        hasRowActions={canWriteProject}
        columnDefinitions={[
          {
            width: "table_cell_xxs",
            title: intl.formatMessage({ id: "column.thumbnail", defaultMessage: "Thumbnail" }),
          },
          {
            width: "table_cell_sm",
            title: intl.formatMessage({ id: "column.title", defaultMessage: "Title" }),
          },
          {
            width: "table_cell_xs",
            title: intl.formatMessage({ id: "column.created_at", defaultMessage: "Created at" }),
          },
          {
            width: "table_cell_xs",
            title: intl.formatMessage({ id: "column.creator", defaultMessage: "Created by" }),
          },
        ]}
        tableDataIds={tableDataIds}
        isSkeleton={isLoading}
        emptyElement={
          onOpenCreateModal ? (
            <RanaTable.Empty icon={iconSrc}>
              <Text
                cursor="pointer"
                textDecor="underline"
                color="brand.analytic"
                mt={2}
                onClick={onOpenCreateModal}
              >
                <FormattedMessage
                  id="projectfiles.publications"
                  defaultMessage="Create publication"
                />
              </Text>
            </RanaTable.Empty>
          ) : (
            <RanaTable.Empty icon={iconSrc} />
          )
        }
      >
        {canWriteProject && (
          <RanaTable.Filters
            checkboxes={[
              {
                label: intl.formatMessage({
                  id: "publications.show_only_mine",
                  defaultMessage: "My publications",
                }),
                checked: showOnlyMine,
                setChecked: setShowOnlyMine,
              },
            ]}
          />
        )}
        <RanaTable.Container>
          <RanaTable.Header />
          <RanaTable.Body>
            {publications.map((publication) => {
              const { id, createdAt, name, creator } = publication;
              return (
                <RanaTable.Row
                  onClick={() => setPublicationId(id)}
                  key={id}
                  rowId={id}
                  rowActions={
                    canWriteProject
                      ? [
                          {
                            label: deletePublicationText,
                            icon: <TrashIcon />,
                            action: () => openDeleteModal([publication]),
                          },
                        ]
                      : undefined
                  }
                >
                  <RanaTable.Cell py={1} rounded="base" overflow="hidden">
                    <PublicationThumbnail publicationId={id} />
                  </RanaTable.Cell>
                  <RanaTable.Cell>{name}</RanaTable.Cell>
                  <RanaTable.Cell title={getPrettyTime(createdAt)}>
                    {getPrettyTimeFromNow(createdAt)}
                  </RanaTable.Cell>
                  <RanaTable.Cell>
                    <Profile user={creator} inTable />
                  </RanaTable.Cell>
                </RanaTable.Row>
              );
            })}
          </RanaTable.Body>
        </RanaTable.Container>

        <RanaTable.Pagination
          limit={limit}
          setPageSize={setPageSize}
          page={page}
          numPages={numPages}
          setPage={setPage}
        />
      </RanaTable>
    </>
  );
}
